// GA4 custom events.
//
// The gtag.js tag itself is loaded in public/index.html; that inline snippet defines `gtag()`
// synchronously (it just pushes onto dataLayer), so calling into it before the async script
// finishes loading is safe — the hit is queued and sent once gtag.js is up.
//
// Everything here is a *custom* event. GA4's automatic measurement only sees native HTML form
// submits and outbound link clicks, which is why none of our real conversions (sign-ups that
// complete, install-command copies, in-app buttons) were showing up: the app submits via fetch
// and its buttons are internal. Each call site below marks the moment an action actually
// succeeded, not the moment it was started.
//
// `sign_up_completed` and `skill_install_copy` are the two to mark as Key events in
// GA4 Admin → Events once these are live.

// GA4 truncates event parameter values at 100 characters — do it here so what we send is what
// lands in the report (and no free-text field can silently lose its tail).
const MAX_PARAM_CHARS = 100;

type ParamValue = string | number | boolean | null | undefined;

/**
 * Send one GA4 event. Null/undefined params are dropped (GA4 would otherwise record them as
 * empty strings), strings are truncated to GA4's limit, and a missing gtag is a no-op so an
 * ad-blocker or a local dev run without the tag can never break a user action.
 */
function track(name: string, params: Record<string, ParamValue> = {}): void {
  // The whole body is guarded, not just the gtag call: several call sites fire an event before
  // the state update that shows the user their action worked (the "Copied" tick, the redirect
  // after signup), so nothing in here may ever throw back into them.
  try {
    if (typeof window === "undefined") return;
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== "function") return;

    const payload: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) continue;
      payload[key] = typeof value === "string" ? value.slice(0, MAX_PARAM_CHARS) : value;
    }

    gtag("event", name, payload);
  } catch {
    /* analytics must never take a user action down with it */
  }
}

export type SignUpMethod = "google" | "email";
// How the visitor took the skill away with them: the agent prompt, the shell command, or the zip.
export type InstallMethod = "prompt" | "command" | "zip";

/** KEY EVENT — an account was actually created (not just the form being focused or started). */
export function trackSignUpCompleted(method: SignUpMethod): void {
  track("sign_up_completed", { method });
}

/** KEY EVENT — the visitor copied/downloaded a skill's install artefact from its page. */
export function trackSkillInstallCopy(skillSlug: string, installMethod: InstallMethod): void {
  track("skill_install_copy", { skill_slug: skillSlug, install_method: installMethod });
}

/** The personalized `curl … | bash` / `irm … | iex` agent-setup command was copied. */
export function trackCliSetupCopy(): void {
  track("cli_setup_copy");
}

/** A search actually ran against the catalog — fired with the result count it produced. */
export function trackSearchSubmit(queryText: string, resultCount: number): void {
  track("search_submit", { query_text: queryText, result_count: resultCount });
}

/** A skill detail page was viewed. */
export function trackSkillView(skillSlug: string, securityScore: number | null): void {
  track("skill_view", { skill_slug: skillSlug, security_score: securityScore });
}

/** The enterprise contact form was sent successfully. */
export function trackContactFormSubmit(): void {
  track("contact_form_submit");
}

/**
 * True when an ISO timestamp is within the last `withinMs`. Backend timestamps come back naive
 * (Mongo stores UTC without an offset), so an absent timezone is treated as UTC rather than
 * local — same idiom the timeAgo helpers use. Guards the Google sign-up event, where the
 * account-creating call and the plain-login call are the same endpoint returning the same shape.
 */
export function isFreshTimestamp(iso: string, withinMs = 60_000): boolean {
  const ms = Date.parse(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(ms)) return false;
  const age = Date.now() - ms;
  return age >= 0 && age < withinMs;
}
